import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Link,
  Stack,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AppleIcon from '@mui/icons-material/Apple';
import AlarmOnRoundedIcon from '@mui/icons-material/AlarmOnRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import config from '../config';
import Reveal from '../components/Reveal';
import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n/LanguageContext';
import type { FeatureKey } from '../i18n/translations';

// Numeric App Store id: a name-only path like /app/chesswiz is not a listing URL.
const IOS_APP_URL = 'https://apps.apple.com/app/id6761688921';
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=net.chesswiz.app';

type FeatureMeta = {
  key: FeatureKey;
  accent: string;
  image: string;
  icon: SvgIconComponent;
};

const featureMeta: FeatureMeta[] = [
  { key: 'email', accent: '#EF4444', image: '/screenshots/real-mobile/email-mistake-alert.png', icon: AlarmOnRoundedIcon },
  { key: 'gamesList', accent: '#0F766E', image: '/screenshots/real-mobile/games-list.png', icon: HistoryRoundedIcon },
  { key: 'practiceHome', accent: '#2E7D32', image: '/screenshots/real-mobile/practice-home.png', icon: ExtensionRoundedIcon },
  { key: 'dailyTraining', accent: '#EA580C', image: '/screenshots/real-mobile/daily-training.png', icon: WhatshotRoundedIcon },
  { key: 'openingPractice', accent: '#0E9F6E', image: '/screenshots/real-mobile/openings-practice.png', icon: MenuBookRoundedIcon },
  { key: 'puzzles', accent: '#D97706', image: '/screenshots/real-mobile/review-puzzle.png', icon: ExtensionRoundedIcon },
  { key: 'analytics', accent: '#2563EB', image: '/screenshots/real-mobile/analytics-dashboard.png', icon: QueryStatsRoundedIcon }
];

const showcaseImages = [
  '/screenshots/real-mobile/games-list.png',
  '/screenshots/real-mobile/openings-practice.png',
  '/screenshots/real-mobile/analytics-dashboard.png'
];

const sectionCardSx = {
  borderRadius: 5,
  border: '1px solid rgba(34, 44, 55, 0.08)',
  boxShadow: '0 24px 80px rgba(15, 23, 42, 0.08)'
};

const openUrl = (url: string) => window.open(url, '_blank');

const HomePage: React.FC = () => {
  const { t } = useI18n();

  const handleDeepLink = (path: string) => {
    if (config.debugMode) {
      console.log(`Deep link attempt: chesswiz://${path}`);
    }

    const appUrl = `chesswiz://${path}`;
    const webUrl = `${config.clientUrl}${path}`;

    window.location.href = appUrl;

    setTimeout(() => {
      if (config.debugMode) {
        console.log(`Fallback to web app: ${webUrl}`);
      }
      window.open(webUrl, '_blank');
    }, 1000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(46, 125, 50, 0.14), transparent 28%), linear-gradient(180deg, #F7F8F5 0%, #FFFFFF 38%, #F4F7FB 100%)'
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 5 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            mb: { xs: 3, md: 7 },
            gap: { xs: 1.5, md: 2 },
            minWidth: 0,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar
              src="/logo192.png"
              alt="ChessWiz"
              sx={{ width: { xs: 42, md: 50 }, height: { xs: 42, md: 50 }, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 0.5, sm: 1.5 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  minWidth: 0
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 800,
                    color: '#17351F',
                    fontSize: { xs: '1.5rem', md: '2.125rem' },
                    lineHeight: 1.05
                  }}
                >
                  ChessWiz
                </Typography>
                <Stack
                  direction="row"
                  spacing={{ xs: 1, sm: 1.5 }}
                  sx={{
                    flexWrap: 'wrap',
                    rowGap: 0.25,
                    fontSize: { xs: '0.72rem', md: '0.875rem' }
                  }}
                >
                  <Link component={RouterLink} to="/how-it-works" color="text.secondary" underline="hover">
                    {t.header.howItWorks}
                  </Link>
                  <Link component={RouterLink} to="/privacy" color="text.secondary" underline="hover">
                    {t.header.privacy}
                  </Link>
                  <Link component={RouterLink} to="/support" color="text.secondary" underline="hover">
                    {t.header.support}
                  </Link>
                  <Link component={RouterLink} to="/terms" color="text.secondary" underline="hover">
                    {t.header.terms}
                  </Link>
                </Stack>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.76rem', md: '0.875rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t.header.tagline}
              </Typography>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 0 }}
          >
            <LanguageSelector />
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiButton-root': {
                  flex: { xs: 1, md: 'initial' },
                  minWidth: 0,
                  px: { xs: 1.25, sm: 2 }
                }
              }}
            >
              <Button
                variant="outlined"
                startIcon={<LoginRoundedIcon />}
                onClick={() => openUrl(config.clientAuthUrls.signIn)}
                sx={{
                  borderRadius: 999,
                  borderColor: 'rgba(23, 53, 31, 0.26)',
                  color: '#17351F',
                  bgcolor: 'rgba(255, 255, 255, 0.72)'
                }}
              >
                {t.header.signIn}
              </Button>
              <Button
                variant="contained"
                startIcon={<PersonAddAlt1RoundedIcon />}
                onClick={() => openUrl(config.clientAuthUrls.signUp)}
                sx={{ borderRadius: 999 }}
              >
                {t.header.createAccount}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Reveal direction="up" duration={800} threshold={0.05} sx={{ mb: { xs: 5, md: 8 } }}>
          <Card
            elevation={0}
            sx={{
              ...sectionCardSx,
              overflow: 'hidden',
              background:
                'linear-gradient(135deg, rgba(23, 53, 31, 0.98) 0%, rgba(39, 83, 48, 0.94) 48%, rgba(233, 240, 232, 0.94) 100%)'
            }}
          >
            <Grid container>
              <Grid item xs={12} lg={6}>
                <Box sx={{ p: { xs: 2.25, sm: 5, md: 6 }, color: '#F8FAFC', minWidth: 0 }}>
                  <Chip
                    label={t.hero.chip}
                    sx={{
                      mb: { xs: 2, md: 3 },
                      bgcolor: 'rgba(248, 250, 252, 0.12)',
                      color: '#F8FAFC',
                      borderRadius: 999,
                      maxWidth: '100%'
                    }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2rem', sm: '3rem', md: '4.25rem' },
                      lineHeight: { xs: 1.02, md: 1 },
                      color: '#FFFFFF',
                      mb: { xs: 1.5, md: 2 },
                      overflowWrap: 'break-word'
                    }}
                  >
                    {t.hero.titleLine1}
                    <br />
                    {t.hero.titleLine2}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      maxWidth: 540,
                      mb: { xs: 2.5, md: 3 },
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: { xs: 1.32, md: 1.45 },
                      overflowWrap: 'break-word'
                    }}
                  >
                    {t.hero.subtitle}
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.25, sm: 2 }}
                    sx={{
                      mb: 2,
                      '& .MuiButton-root': {
                        minHeight: { xs: 42, sm: 48 },
                        fontSize: { xs: '0.88rem', sm: '0.9375rem' }
                      }
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<AppleIcon />}
                      onClick={() => openUrl(IOS_APP_URL)}
                      sx={{
                        bgcolor: '#F8FAFC',
                        color: '#17351F',
                        '&:hover': { bgcolor: '#E2E8F0' }
                      }}
                    >
                      {t.hero.getIos}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PhoneAndroidIcon />}
                      onClick={() => openUrl(ANDROID_APP_URL)}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.35)',
                        color: '#FFFFFF',
                        '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.06)' }
                      }}
                    >
                      {t.hero.getAndroid}
                    </Button>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.25, sm: 2 }}
                    sx={{
                      mb: { xs: 0, md: 4 },
                      '& .MuiButton-root': {
                        minHeight: { xs: 40, sm: 48 },
                        fontSize: { xs: '0.84rem', sm: '0.9375rem' }
                      }
                    }}
                  >
                    <Button
                      variant="text"
                      size="large"
                      startIcon={<LoginRoundedIcon />}
                      onClick={() => openUrl(config.clientAuthUrls.signIn)}
                      sx={{
                        color: '#FFFFFF',
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' }
                      }}
                    >
                      {t.hero.signInWeb}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PersonAddAlt1RoundedIcon />}
                      onClick={() => openUrl(config.clientAuthUrls.signUp)}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.35)',
                        color: '#FFFFFF',
                        '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.06)' }
                      }}
                    >
                      {t.hero.createWebAccount}
                    </Button>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    height: '100%',
                    display: { xs: 'none', lg: 'flex' },
                    alignItems: 'center'
                  }}
                >
                  <Grid container spacing={2}>
                    {showcaseImages.map((imagePath, index) => (
                      <Grid item xs={12} sm={4} key={imagePath}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 5,
                            bgcolor: index === 1 ? '#FFFFFF' : 'rgba(255,255,255,0.82)',
                            transform: { sm: index === 1 ? 'translateY(-18px)' : 'none' },
                            boxShadow: '0 30px 70px rgba(15, 23, 42, 0.2)'
                          }}
                        >
                          <Box
                            component="img"
                            src={imagePath}
                            alt={`ChessWiz feature preview ${index + 1}`}
                            sx={{
                              width: '100%',
                              display: 'block',
                              borderRadius: 4,
                              bgcolor: '#E5E7EB'
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Reveal>

        <Box sx={{ mb: { xs: 5, md: 8 } }}>
          <Reveal direction="up" sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" gutterBottom>
              {t.featuresSection.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto' }}>
              {t.featuresSection.subtitle}
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {featureMeta.map((feature, featureIndex) => {
              const Icon = feature.icon;
              const copy = t.features[feature.key];

              return (
                <Grid item xs={12} md={6} lg={4} key={feature.key}>
                  <Reveal direction="up" delay={(featureIndex % 3) * 90} sx={{ height: '100%' }}>
                    <Card elevation={0} sx={{ ...sectionCardSx, height: '100%', bgcolor: '#FFFFFF' }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: 3,
                              bgcolor: `${feature.accent}14`
                            }}
                          >
                            <Icon sx={{ color: feature.accent, fontSize: 28 }} />
                          </Box>
                          <Box>
                            <Typography
                              variant="overline"
                              sx={{ color: feature.accent, letterSpacing: '0.08em' }}
                            >
                              {copy.eyebrow}
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#18212F' }}>
                              {copy.title}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                          {copy.description}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                          {copy.bullets.map((bullet) => (
                            <Chip
                              key={bullet}
                              label={bullet}
                              size="small"
                              sx={{
                                bgcolor: '#F8FAFC',
                                border: '1px solid rgba(99, 115, 129, 0.2)'
                              }}
                            />
                          ))}
                        </Stack>
                        <Box
                          sx={{
                            p: { xs: 1.25, md: 1.5 },
                            borderRadius: 4,
                            bgcolor: `${feature.accent}10`,
                            border: '1px solid rgba(99, 115, 129, 0.16)',
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            component="img"
                            src={feature.image}
                            alt={copy.title}
                            sx={{
                              width: 'auto',
                              maxWidth: { xs: '100%', md: '72%', xl: '62%' },
                              height: { xs: 420, md: 360, xl: 320 },
                              display: 'block',
                              borderRadius: 3,
                              objectFit: 'contain',
                              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)'
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Reveal>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Strategy Coach & AI */}
        <Reveal direction="up" sx={{ mb: { xs: 5, md: 8 } }}>
          <Card
            elevation={0}
            sx={{
              ...sectionCardSx,
              overflow: 'hidden',
              background:
                'linear-gradient(135deg, rgba(88, 28, 135, 0.96) 0%, rgba(67, 56, 202, 0.92) 55%, rgba(238, 242, 255, 0.96) 100%)'
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 5, md: 6 }, color: '#F8FAFC' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2.5,
                        bgcolor: 'rgba(248, 250, 252, 0.16)'
                      }}
                    >
                      <PsychologyRoundedIcon sx={{ color: '#FFFFFF', fontSize: 26 }} />
                    </Box>
                    <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'rgba(255,255,255,0.85)' }}>
                      {t.coach.eyebrow}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{ color: '#FFFFFF', fontSize: { xs: '1.75rem', md: '2.5rem' }, mb: 2 }}
                  >
                    {t.coach.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.82)',
                      fontWeight: 400,
                      maxWidth: 520,
                      mb: 3,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      lineHeight: 1.5
                    }}
                  >
                    {t.coach.description}
                  </Typography>
                  <Stack spacing={1.25}>
                    {t.coach.bullets.map((bullet) => (
                      <Stack key={bullet} direction="row" spacing={1.25} alignItems="center">
                        <AutoAwesomeRoundedIcon sx={{ color: '#FBBF24', fontSize: 20 }} />
                        <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>{bullet}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: { xs: 1.5, md: 2 }
                  }}
                >
                  {[
                    { src: '/screenshots/real-mobile/strategic-coach.png', offset: true },
                    { src: '/screenshots/real-mobile/coach-ai.png', offset: false }
                  ].map((shot, index) => (
                    <Box
                      key={shot.src}
                      sx={{
                        p: 1,
                        borderRadius: 4,
                        bgcolor: '#FFFFFF',
                        flex: 1,
                        maxWidth: 260,
                        transform: { md: shot.offset ? 'translateY(-16px)' : 'translateY(16px)' },
                        boxShadow: '0 30px 70px rgba(15, 23, 42, 0.28)'
                      }}
                    >
                      <Box
                        component="img"
                        src={shot.src}
                        alt={`ChessWiz coaching preview ${index + 1}`}
                        sx={{ width: '100%', display: 'block', borderRadius: 3, bgcolor: '#E5E7EB' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Reveal>

        {/* Tournaments */}
        <Reveal direction="up" sx={{ mb: { xs: 5, md: 8 } }}>
          <Card elevation={0} sx={{ ...sectionCardSx, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
            <Grid container alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 420,
                      borderRadius: 5,
                      p: { xs: 2.5, md: 3.5 },
                      color: '#FFFFFF',
                      background:
                        'linear-gradient(150deg, #B45309 0%, #D97706 45%, #F59E0B 100%)',
                      boxShadow: '0 24px 60px rgba(180, 83, 9, 0.28)'
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                      <EmojiEventsRoundedIcon sx={{ fontSize: 40, color: '#FFFBEB' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.1 }}>
                          Weekly Blitz Arena
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                          9 rounds · 6.5 / 9
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack spacing={1}>
                      {[
                        { round: 'Round 3', line: 'Sicilian Defense', result: 'Win', color: '#DCFCE7', text: '#166534' },
                        { round: 'Round 5', line: 'Ruy López', result: 'Draw', color: '#FEF9C3', text: '#854D0E' },
                        { round: 'Round 7', line: 'French Defense', result: 'Loss', color: '#FEE2E2', text: '#991B1B' }
                      ].map((row) => (
                        <Stack
                          key={row.round}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.14)',
                            borderRadius: 2.5,
                            px: 1.75,
                            py: 1.1
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{row.round}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                              {row.line}
                            </Typography>
                          </Box>
                          <Chip
                            label={row.result}
                            size="small"
                            sx={{ bgcolor: row.color, color: row.text, fontWeight: 700 }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 5, md: 6 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2.5,
                        bgcolor: 'rgba(217, 119, 6, 0.12)'
                      }}
                    >
                      <EmojiEventsRoundedIcon sx={{ color: '#B45309', fontSize: 26 }} />
                    </Box>
                    <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#B45309' }}>
                      {t.tournaments.eyebrow}
                    </Typography>
                  </Stack>
                  <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, mb: 2, color: '#18212F' }}>
                    {t.tournaments.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontWeight: 400, maxWidth: 520, mb: 3, fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.5 }}
                  >
                    {t.tournaments.description}
                  </Typography>
                  <Stack spacing={1.25}>
                    {t.tournaments.bullets.map((bullet) => (
                      <Stack key={bullet} direction="row" spacing={1.25} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: '#B45309', fontSize: 20 }} />
                        <Typography sx={{ color: '#18212F', fontWeight: 500 }}>{bullet}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Reveal>

        {config.debugMode && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
              Deep Link Testing
              <Chip label="DEV ONLY" color="warning" size="small" sx={{ ml: 2 }} />
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleDeepLink('/moves/688f728326cbcbd5b399c85c')}
                  startIcon={<SportsEsportsIcon />}
                >
                  Test Move Detail Link
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleDeepLink('/games')}
                  startIcon={<SportsEsportsIcon />}
                >
                  Test Games Link
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleDeepLink('/settings')}
                  startIcon={<AutoGraphRoundedIcon />}
                >
                  Test Settings Link
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: '1px solid #DDE5DB' }}>
          <Typography variant="body2" color="text.secondary">
            {t.footer.rights}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <Link component={RouterLink} to="/how-it-works" color="text.secondary" underline="hover">
              {t.footer.howItWorks}
            </Link>
            <Link component={RouterLink} to="/privacy" color="text.secondary" underline="hover">
              {t.footer.privacy}
            </Link>
            <Link component={RouterLink} to="/support" color="text.secondary" underline="hover">
              {t.footer.support}
            </Link>
            <Link component={RouterLink} to="/terms" color="text.secondary" underline="hover">
              {t.footer.terms}
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
